import { useState } from 'react';
import { X, Upload, Loader2, AlertCircle, CheckCircle, FileSpreadsheet, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { purchasingAPI, ContractPriceImportPreview } from '../../lib/api';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../utils/errorUtils';
import FileDropZone from '../common/FileDropZone';

interface ContractPriceImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
}

type ImportStep = 'upload' | 'preview' | 'result';

export default function ContractPriceImportModal({
  isOpen,
  onClose,
  onImportComplete
}: ContractPriceImportModalProps) {
  const [step, setStep] = useState<ImportStep>('upload');
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<ContractPriceImportPreview[]>([]);
  const [errors, setErrors] = useState<Array<{ row: number; errors: string[] }>>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [importResult, setImportResult] = useState<{
    created_contracts: number;
    created_parts: number;
    updated_parts: number;
    errors: Array<{ contract_number: string; error: string }>;
  } | null>(null);

  // Customer review 11 Sep (drag and drop everywhere): FileDropZone enforces
  // .xlsx/.xls/.csv for drops and picks, naming any refused file.
  const handleFileSelect = (files: File[]) => {
    if (files[0]) setFile(files[0]);
  };

  // CR-7383: the blank template, headed exactly as the importer expects.
  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      const response = await purchasingAPI.downloadContractPriceImportTemplate();
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'price_book_import_template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (err: unknown) {
      console.error('Template download failed:', err);
      toast.error(getApiErrorMessage(err, 'Failed to download template'));
    } finally {
      setDownloading(false);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await purchasingAPI.importContractPricesPreview(formData);

      if (response.data.status === 'success') {
        setPreviewData(response.data.preview);
        setErrors(response.data.errors);
        setTotalRows(response.data.total_rows);
        setStep('preview');
      } else {
        toast.error(response.data.message || 'Failed to preview import');
      }
    } catch (err: unknown) {
      console.error('Import preview failed:', err);
      toast.error(getApiErrorMessage(err, 'Failed to process file'));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmImport = async () => {
    // Filter out rows with errors
    const validRows = previewData.filter(row => row.errors.length === 0);

    if (validRows.length === 0) {
      toast.error('No valid rows to import');
      return;
    }

    setLoading(true);
    try {
      const response = await purchasingAPI.importContractPricesConfirm({ rows: validRows });

      if (response.data.status === 'success') {
        setImportResult(response.data);
        setStep('result');
        toast.success('Import completed successfully');
      } else {
        toast.error(response.data.message || 'Import failed');
      }
    } catch (err: unknown) {
      console.error('Import confirmation failed:', err);
      toast.error(getApiErrorMessage(err, 'Failed to import data'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (step === 'result') {
      onImportComplete();
    }
    setStep('upload');
    setFile(null);
    setPreviewData([]);
    setErrors([]);
    setImportResult(null);
    onClose();
  };

  const resetToUpload = () => {
    setStep('upload');
    setFile(null);
    setPreviewData([]);
    setErrors([]);
  };

  if (!isOpen) return null;

  const validRowCount = previewData.filter(row => row.errors.length === 0).length;
  const errorRowCount = previewData.filter(row => row.errors.length > 0).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">
              {step === 'upload' && 'Import Contract Prices'}
              {step === 'preview' && 'Preview Import Data'}
              {step === 'result' && 'Import Complete'}
            </h2>
          </div>
          <button onClick={handleClose} className="p-1 hover:bg-muted rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Upload */}
          {step === 'upload' && (
            <div className="space-y-6">
              <div className="text-center">
                <p className="text-muted-foreground mb-4">
                  Upload an Excel or CSV file to import contract pricing data.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadTemplate}
                  disabled={downloading}
                  className="inline-flex items-center gap-2"
                  data-testid="contract-price-import-download-template"
                >
                  {downloading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  Download Template
                </Button>
              </div>

              {/* File Drop Zone */}
              <FileDropZone
                accept=".xlsx,.xls,.csv"
                hint="Supported formats: .xlsx, .xls, .csv"
                onFiles={handleFileSelect}
                className="p-8"
                data-testid="contract-price-import-drop-zone"
              >
                {file && (
                  <p className="text-sm">
                    <span className="font-medium text-primary">{file.name}</span>
                    <span className="text-muted-foreground"> · {(file.size / 1024).toFixed(1)} KB</span>
                  </p>
                )}
              </FileDropZone>

              {/* Required Columns Info */}
              <div className="bg-muted/30 rounded-lg p-4">
                <h3 className="font-medium mb-2">Required Columns</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-muted-foreground">
                  <span>Contract_Number *</span>
                  <span>Contract_Type</span>
                  <span>Customer_Company *</span>
                  <span>Part_Number *</span>
                  <span>Description</span>
                  <span>Unit_Of_Measure</span>
                  <span>Part_DrawingIssue</span>
                  <span>Qty_From *</span>
                  <span>Qty_To *</span>
                  <span>Unit_Price or Lot_Price *</span>
                  <span>Supplier_Code *</span>
                  <span>Valid_From *</span>
                  <span>Valid_To *</span>
                  <span>Currency *</span>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  * required. You can include many contracts in one file —
                  rows are grouped by Contract_Number. Add a Contract_Type column
                  (the 3-letter code of one of your own contract types, e.g. STD)
                  and any row that leaves Contract_Number blank is numbered
                  automatically, exactly as Add Contract does; a row that states a
                  number keeps it unchanged. Currency is taken from the
                  supplier code where one is on record: a row that states a
                  different currency is rejected, and a row that states none
                  inherits it. Only where the code has no currency recorded must
                  the row state one itself — it is never assumed
                  (supported: GBP, USD, EUR, JPY, CNY, INR, AUD, CAD, CHF, MXN, PLN).
                </p>
              </div>
            </div>
          )}

          {/* Step 2: Preview */}
          {step === 'preview' && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="flex items-center gap-4 p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Total Rows:</span>
                  <span className="font-semibold">{totalRows}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-sm">Valid: {validRowCount}</span>
                </div>
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-red-500" />
                  <span className="text-sm">Errors: {errorRowCount}</span>
                </div>
              </div>

              {/* Errors Summary */}
              {errors.length > 0 && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <h4 className="font-medium text-red-800 mb-2">Validation Errors</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {errors.slice(0, 10).map((err, idx) => (
                      <div key={idx} className="text-sm text-red-700">
                        <span className="font-medium">Row {err.row}:</span> {err.errors.join(', ')}
                      </div>
                    ))}
                    {errors.length > 10 && (
                      <p className="text-sm text-red-600 mt-2">
                        And {errors.length - 10} more errors...
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Preview Table */}
              <div className="border rounded-lg overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left py-2 px-3 font-medium">Row</th>
                      <th className="text-left py-2 px-3 font-medium">Status</th>
                      <th className="text-left py-2 px-3 font-medium">Contract #</th>
                      <th className="text-left py-2 px-3 font-medium">Type</th>
                      <th className="text-left py-2 px-3 font-medium">Customer</th>
                      <th className="text-left py-2 px-3 font-medium">Part #</th>
                      <th className="text-left py-2 px-3 font-medium">Drawing Issue</th>
                      <th className="text-left py-2 px-3 font-medium">Currency</th>
                      <th className="text-right py-2 px-3 font-medium">Unit Price</th>
                      <th className="text-right py-2 px-3 font-medium">Qty Range</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.slice(0, 50).map((row, idx) => (
                      <tr key={idx} className={`border-b ${row.errors.length > 0 ? 'bg-red-50' : ''}`}>
                        <td className="py-2 px-3">{row.row}</td>
                        <td className="py-2 px-3">
                          {row.errors.length > 0 ? (
                            <span className="text-red-600 flex items-center gap-1">
                              <AlertCircle className="h-3 w-3" />
                              Error
                            </span>
                          ) : (
                            <span className="text-green-600 flex items-center gap-1">
                              <CheckCircle className="h-3 w-3" />
                              Valid
                            </span>
                          )}
                        </td>
                        {/* CR-7298: a blank number is not missing data — the
                            row is asking to be numbered from its contract type,
                            so say so rather than showing an empty cell. */}
                        <td className="py-2 px-3 font-mono" data-testid="preview-contract-number">
                          {row.contract_number || (
                            <span className="text-muted-foreground italic">auto</span>
                          )}
                        </td>
                        <td className="py-2 px-3 font-mono" data-testid="preview-contract-type">
                          {row.contract_type_code || row.contract_type || '—'}
                        </td>
                        <td className="py-2 px-3">{row.customer_name || row.customer}</td>
                        <td className="py-2 px-3 font-mono">{row.part_number}</td>
                        {/* CR-7387: saved with the part; blank is a part of its own. */}
                        <td className="py-2 px-3 font-mono" data-testid="preview-drawing-issue">
                          {row.part_drawing_issue || '—'}
                        </td>
                        {/* CR-7272: the currency each row will price in — blank is
                            shown as a dash and the row carries a "Currency is
                            required" error, so nothing is ever assumed to be GBP. */}
                        <td className="py-2 px-3 font-mono" data-testid="preview-currency">
                          {row.currency || '—'}
                        </td>
                        <td className="py-2 px-3 text-right">
                          {row.unit_price != null ? row.unit_price : row.lot_price || '-'}
                        </td>
                        <td className="py-2 px-3 text-right">{row.qty_from} - {row.qty_to}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {previewData.length > 50 && (
                  <div className="text-center py-2 text-sm text-muted-foreground border-t">
                    Showing first 50 of {previewData.length} rows
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Result */}
          {step === 'result' && importResult && (
            <div className="space-y-6 text-center">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
              <h3 className="text-xl font-semibold">Import Completed Successfully!</h3>

              <div className="grid grid-cols-3 gap-4 max-w-md mx-auto">
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{importResult.created_contracts}</p>
                  <p className="text-sm text-muted-foreground">Contracts Created</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{importResult.created_parts}</p>
                  <p className="text-sm text-muted-foreground">Parts Created</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg">
                  <p className="text-2xl font-bold text-amber-600">{importResult.updated_parts}</p>
                  <p className="text-sm text-muted-foreground">Parts Updated</p>
                </div>
              </div>

              {importResult.errors.length > 0 && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg max-w-lg mx-auto">
                  <h4 className="font-medium text-red-800 mb-2">Some records failed:</h4>
                  <div className="space-y-1 text-left">
                    {importResult.errors.map((err, idx) => (
                      <p key={idx} className="text-sm text-red-700">
                        {err.contract_number}: {err.error}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-4 border-t">
          {step === 'preview' && (
            <Button variant="outline" onClick={resetToUpload} disabled={loading}>
              Back
            </Button>
          )}
          <div className="flex-1" />
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            {step === 'result' ? 'Done' : 'Cancel'}
          </Button>

          {step === 'upload' && (
            <Button onClick={handleUpload} disabled={!file || loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Preview Import
                </>
              )}
            </Button>
          )}

          {step === 'preview' && (
            <Button onClick={handleConfirmImport} disabled={validRowCount === 0 || loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Import {validRowCount} Records
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
